import { Flex, Icon, Text } from '@/ui';
import { Option, SelectDropdown } from './SelectDropdown';

// Reusable compact dropdown for use as the trailing control in a SidebarRow.
// Anchors the menu to the right edge (left: 'auto') so it never overflows
// the panel when the trigger is narrower than minW.

// Generic over the option id so a control bound with `register` keeps the
// field's own type (an enum, usually) instead of widening to string.
type RowOption<T extends string> = { id: T; label: string };

type Props<T extends string> = {
  /** Shows the current value without letting it be changed. */
  isDisabled?: boolean;
  /**
   * Option ids that need a paid plan: crowned in the menu, and clicking one
   * routes to `onLockedClick` instead of selecting it. See SelectDropdown's
   * `Option`.
   */
  lockedIds?: readonly T[];
  minW?: string;
  onChange: (id: T) => void;
  /** Runs instead of `onChange` when a locked option is clicked. */
  onLockedClick?: (id: T) => void;
  /** Previews the hovered option (null when nothing is hovered). */
  onPreview?: (id: T | null) => void;
  options: RowOption<T>[];
  value: T;
};

export const SidebarRowSelect = <T extends string>({
  isDisabled,
  lockedIds,
  minW = '120px',
  onChange,
  onLockedClick,
  onPreview,
  options,
  value,
}: Props<T>) => (
  <SelectDropdown
    isDisabled={isDisabled}
    menuProps={{ left: 'auto', minW, right: 0 }}
    options={options}
    triggerProps={{ px: 2.5, py: 1.5 }}
    value={value}
    renderOption={(option, isActive) => {
      const isLocked = lockedIds?.includes(option.id) ?? false;

      return (
        <Option
          isActive={isActive}
          isLocked={isLocked}
          key={option.id}
          option={option}
          onLockedClick={onLockedClick && (() => onLockedClick(option.id))}
        >
          <Text
            color={isActive ? 'magicPurple.700' : 'ink.strong'}
            fontSize="ah-control"
            fontWeight={isActive ? 600 : 500}
          >
            {option.label}
          </Text>
        </Option>
      );
    }}
    renderTrigger={(active) => (
      // The crown rides the trigger too: a locked option that is already the
      // stored value would otherwise only announce itself once the menu is
      // open, which is exactly the case the reader most needs to see.
      <Flex align="center" gap={1.5} minW={0}>
        <Text
          color="ink.strong"
          fontSize="ah-control"
          fontWeight={500}
          whiteSpace="nowrap"
        >
          {active.label}
        </Text>
        {lockedIds?.includes(active.id) && (
          <Icon
            color="magicPurple.500"
            flexShrink={0}
            icon="Crown"
            size={3.5}
          />
        )}
      </Flex>
    )}
    onChange={onChange}
    onPreview={onPreview}
  />
);
